import { ArrowRight } from "lucide-react";
import { BRAND_WAYS } from "@/data/site";
import { Reveal } from "@/components/Reveal";
import { windowState } from "@/lib/windows";
import { PageHeader, Kicker } from "@/components/Section";
import { CTA } from "@/components/CTA";
import { Spark } from "@/components/Doodles";
import { MissionCards } from "@/components/transmission/MissionCards";
import { ClassifiedDossier } from "@/components/transmission/ClassifiedDossier";
import { PartnerWall } from "@/components/transmission/PartnerWall";

export default function BrandPartners() {
  return (
    <div className="bg-onyx text-cream">
      <PageHeader
        kicker="For Brands"
        titleLines={["Give students", "a real brief."]}
        subtitle="Support emerging creatives through project-based partnerships that turn real business needs into supervised, portfolio-building experiences — and paid creative opportunities."
        dark
      />

      <section className="grain-dark relative border-b border-gold/20 bg-charcoal py-20 sm:py-28">
        <div className="halftone-gold pointer-events-none absolute inset-0 opacity-[0.03]" />
        <div className="relative mx-auto max-w-[1400px] px-4 sm:px-6">
          <Reveal><Kicker>Ways to Participate</Kicker></Reveal>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {BRAND_WAYS.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.06}>
                <div className="group h-full border border-silver/20 bg-onyx/60 p-6 transition-all duration-300 hover:-translate-y-1 hover:border-gold/50 hover:shadow-[0_0_30px_rgba(201,162,39,0.12)]">
                  <Spark className="h-7 w-7" />
                  <h3 className="mt-4 font-head text-xl font-extrabold uppercase tracking-tight text-cream transition-colors group-hover:text-goldlight">{w.title}</h3>
                  <p className="mt-2 font-body text-sm leading-relaxed text-silver/75">{w.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="grain-dark relative border-b border-gold/20 bg-onyx py-20 sm:py-28">
        <div className="halftone-gold pointer-events-none absolute inset-0 opacity-[0.03]" />
        <div className="relative mx-auto max-w-[1400px] px-4 sm:px-6">
          <Reveal>
            <Kicker>Live Mission Files</Kicker>
            <h2 className="mt-5 font-display text-3xl uppercase leading-[0.95] tracking-tight text-metallic sm:text-5xl">
              Missions awaiting <span className="text-gold-metallic">partners.</span>
            </h2>
            <p className="mt-4 max-w-xl font-body text-base leading-relaxed text-silver/80">
              Sample operations showing how a product, a room, or a camera becomes an emerging creative's first real campaign.
            </p>
          </Reveal>
          <div className="mt-10"><MissionCards ctaTo="/transmission/industry" /></div>
        </div>
      </section>

      <section className="grain-dark bg-onyx py-20 sm:py-28">
        <div className="relative mx-auto grid max-w-[1400px] items-start gap-12 px-4 sm:px-6 lg:grid-cols-[0.9fr_1.1fr]">
          <Reveal>
            <Kicker>Submit a Brief</Kicker>
            <h2 className="mt-5 font-display text-4xl uppercase leading-[0.9] tracking-tight text-metallic sm:text-5xl">
              Turn a business need into a <span className="text-gold-metallic">campaign.</span>
            </h2>
            <p className="mt-6 font-body text-lg leading-relaxed text-silver/85">
              We'll match your brief with a cohort of emerging creatives and supervise the work end to end.
              Compensation may vary by project — we clearly distinguish portfolio-based founding projects from
              paid and sponsored opportunities.
            </p>
          </Reveal>

          <Reveal delay={0.12}>
            <div id="inquiry" className="gold-frame grain-dark relative bg-charcoal p-8 sm:p-10">
              <span className="absolute -top-4 right-6 -rotate-3 border border-gold/50 bg-onyx px-2.5 py-0.5 font-marker text-lg text-goldlight">big brief energy ✦</span>
              <Kicker>Partnership Inquiry</Kicker>
              <h3 className="mt-5 font-display text-4xl uppercase leading-[0.9] tracking-tight text-metallic">Bring the Village a real idea.</h3>
              <p className="mt-4 font-body leading-relaxed text-silver/80">
                Our full inquiry adapts to how you want to partner — real briefs, Issue sponsorship, tools,
                locations, mentors, or opportunities. Pick your paths and the form unlocks the right questions.
              </p>
              <CTA to="/apply/brand" variant="pink" className="mt-8" data-testid="brand-inquiry-cta">
                {windowState("brand").status === "open" ? "Request Network Clearance" : "Review the Network File"} <ArrowRight size={18} />
              </CTA>
              {windowState("brand").status !== "open" && (
                <p className="mt-3 inline-block border border-gold/50 bg-onyx px-3 py-1.5 font-mono text-[9px] font-bold uppercase tracking-[0.25em] text-goldlight" data-testid="brand-restricted-chip">
                  Network access restricted · Opens {windowState("brand").opensLabel}
                </p>
              )}
              <p className="mt-4 font-body text-xs text-silver/50">
                An inquiry does not create an agreement — scope, compensation, usage, and credits are established separately.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="grain-dark border-t border-gold/20 bg-charcoal py-20 sm:py-28">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6">
          <Reveal>
            <Kicker>The Mission Wall</Kicker>
            <h2 className="mt-5 font-display text-3xl uppercase leading-[0.95] tracking-tight text-metallic sm:text-5xl">
              Missions <span className="text-gold-metallic">activated.</span>
            </h2>
            <p className="mt-4 max-w-xl font-body text-base leading-relaxed text-silver/80">
              The partners whose resources became real campaigns for emerging creatives.
            </p>
          </Reveal>
          <div className="mt-10"><PartnerWall /></div>
        </div>
      </section>

      <section className="grain-dark border-t border-gold/20 bg-charcoal py-16 text-center sm:py-20">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6">
          <Reveal><Kicker>Intercepted for you</Kicker></Reveal>
          <div className="mt-8"><ClassifiedDossier kind="industry" /></div>
        </div>
      </section>
    </div>
  );
}
